import React, { useState, useEffect, useCallback } from "react";
import { ThemeProvider } from "@material-ui/core/styles";
import { globalTheme } from "style/globalTheme";
import styled from "styled-components";
import { brandPalette } from "brand/theme/brandTheme";
import { Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, TablePagination, CircularProgress, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { formatData } from "utilities/datatable";
import { config } from "utilities/config";
import { post } from "app/services/api";
import ViewProfileRequestById from "./ViewProfileRequestById";
import { useHistory } from "react-router-dom";

const Heading = styled.div`
  height: 15rem;
  padding: 1rem 1.2rem;
  background-image: linear-gradient(
      to right bottom,
      ${brandPalette["Complementary"][200]},
      ${brandPalette["Analogous-1"][600]}
    ),
    url(brand/slide4.jpeg);
  clip-path: polygon(0 0, 100% 0, 100% 80%, 0% 100%);
  display: flex;
  align-items: flex-end;

  h2 {
    font-size: 2rem;
    letter-spacing: 0.07rem;
    text-transform: uppercase;
    font-weight: 700;
    color: white;
    background-image: linear-gradient(
      to right,
      rgba(60, 60, 60, 0.5),
      rgba(0, 28, 48, 0.5)
    );
    padding: 0.5rem 1.5rem;
    border-radius: 8px;
    margin: 0 0 1.5rem 0;
  }
`;

const Content = styled.section`
  padding: 2rem 1rem;
  min-height: 30rem;
  background-color: #fafafa;
`;

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(3),
    boxShadow: theme.shadows[2],
  },
  table: {
    minWidth: 650,
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& .MuiTableCell-head": {
      color: theme.palette.common.white,
      fontWeight: 600,
    },
  },
  actionButton: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    textTransform: "none",
    minWidth: "auto",
    padding: theme.spacing(0.5, 1),
  },
  actionCell: {
    whiteSpace: "nowrap",
    padding: theme.spacing(1),
  },
  statusPending: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  statusApproved: {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  statusRejected: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  goBackButton: {
    color: "#424242",
    textTransform: "none",
    fontSize: "1rem",
    padding: "8px 16px",
    borderRadius: "4px",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const ViewProfileRequests = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const contactid = useSelector((state) => state.contactid);
  const [profileRequests, setProfileRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleGoBack = () => {
    history.push("/");
  };

  const fetchProfileRequests = useCallback(() => {
    setLoading(true);
    const api = `${config.teleportal.env}${config.teleportal.submitForm.get}`;
    const payload = {
      recordtype: "ctpt_employeeprofilerequestses",
      idfield: "ctpt_employeeprofilerequestsid",
      properties: [
        "ctpt_employeeprofilerequestsid",
        "ctpt_name",
        "ctpt_daterequested",
        "ctpt_approved",
        "ctpt_rejected",
        "ctpt_reasonforcontacting",
        "ctpt_employer",
        "ctpt_employee"
      ],
      formattedFields: [
        "_ctpt_employer_value",
        "_ctpt_employee_value"
      ],
      filter: {
        globalfilter: "",
        globalAndOperator: false,
        fieldAndOperator: false,
        filterfields: [
          {
            field: "_ctpt_employee_value",
            searchvalue: contactid,
            type: "guid"
          }
        ]
      },
      continuationToken: "",
      pazesize: "1000"
    };

    axios.post(api, payload, {
      headers: {
        "Authorization": "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json"
      }
    })
      .then((res) => {
        console.log("Full API Response:", JSON.stringify(res.data, null, 2)); // Debug log
        try {
          // Handle both lowercase 'data' and uppercase 'Data' for compatibility
          const responseData = res.data?.data || res.data?.Data;

          if (!res.data) {
            console.error("No data in response");
            setProfileRequests([]);
            setLoading(false);
            return;
          }

          if (!responseData || !Array.isArray(responseData)) {
            console.error("Response data is not an array:", responseData);
            console.log("Response structure:", {
              hasData: !!res.data,
              responseData: responseData,
              type: typeof responseData,
              isArray: Array.isArray(responseData)
            });
            setProfileRequests([]);
            setLoading(false);
            return;
          }

          // The API returns: { continuationToken: "", data: [...] }
          // Each item in data array has: { id, aggregateData, continuationToken, data: [...] }
          // The data array contains field objects with name/value pairs OR another nested structure

          // Extract records from the nested structure
          const extractedRecords = [];

          responseData.forEach((outerItem, outerIndex) => {
            console.log(`Processing outer item ${outerIndex}:`, outerItem);

            if (outerItem && outerItem.data && Array.isArray(outerItem.data)) {
              // Check if the data array contains field objects (has 'name' property) or nested items
              const firstItem = outerItem.data[0];

              if (firstItem && firstItem.name !== undefined) {
                // The data array directly contains field objects (name/value pairs)
                // This is the record we want
                const record = {
                  id: outerItem.id,
                  data: outerItem.data
                };
                console.log(`Extracted record (direct field objects):`, record);
                extractedRecords.push(record);
              } else if (firstItem && firstItem.data && Array.isArray(firstItem.data)) {
                // The data array contains nested items, check if those contain field objects
                outerItem.data.forEach((innerItem, innerIndex) => {
                  console.log(`Processing inner item ${innerIndex}:`, innerItem);

                  if (innerItem && innerItem.data && Array.isArray(innerItem.data)) {
                    const firstField = innerItem.data[0];
                    if (firstField && firstField.name !== undefined) {
                      // This inner item's data array contains field objects
                      const record = {
                        id: innerItem.id || outerItem.id,
                        data: innerItem.data
                      };
                      console.log(`Extracted record (nested field objects):`, record);
                      extractedRecords.push(record);
                    } else {
                      console.log(`Inner item ${innerIndex} data doesn't contain field objects:`, innerItem.data);
                    }
                  } else {
                    console.log(`Inner item ${innerIndex} doesn't have data array:`, innerItem);
                  }
                });
              } else {
                console.log(`Outer item ${outerIndex} data structure not recognized:`, outerItem.data);
              }
            } else {
              console.log(`Outer item ${outerIndex} doesn't have expected structure:`, outerItem);
            }
          });

          console.log("Total extracted records:", extractedRecords.length);
          console.log("Extracted Records:", extractedRecords);

          if (extractedRecords.length > 0) {
            // Format the extracted records
            const wrappedResponse = { data: extractedRecords };
            const formattedRequests = formatData(wrappedResponse);
            console.log("Formatted Requests:", formattedRequests);
            console.log("Formatted Requests count:", formattedRequests.length);

            if (formattedRequests && formattedRequests.length > 0) {
              const activeRequests = formattedRequests.filter((request) => !(
                request.ctpt_rejected === true ||
                request.ctpt_rejected === "true" ||
                request.ctpt_rejected === 1 ||
                request.ctpt_rejected === "1"
              ));
              setProfileRequests(activeRequests);
            } else {
              console.error("formatData returned empty array");
              // Try fallback: use formatData directly on response
              console.log("Trying fallback: using formatData directly on response");
              try {
                const fallbackResponse = { data: responseData };
                const fallbackFormatted = formatData(fallbackResponse);
                console.log("Fallback formatted:", fallbackFormatted);
                if (fallbackFormatted && fallbackFormatted.length > 0) {
                  const activeRequests = fallbackFormatted.filter((request) => !(
                    request.ctpt_rejected === true ||
                    request.ctpt_rejected === "true" ||
                    request.ctpt_rejected === 1 ||
                    request.ctpt_rejected === "1"
                  ));
                  setProfileRequests(activeRequests);
                } else {
                  setProfileRequests([]);
                }
              } catch (fallbackError) {
                console.error("Fallback also failed:", fallbackError);
                setProfileRequests([]);
              }
            }
          } else {
            console.warn("No records extracted from response. Trying fallback...");
            // Try fallback: use formatData directly on response
            try {
              const fallbackResponse = { data: responseData };
              const fallbackFormatted = formatData(fallbackResponse);
              console.log("Fallback formatted:", fallbackFormatted);
              if (fallbackFormatted && fallbackFormatted.length > 0) {
                const activeRequests = fallbackFormatted.filter((request) => !(
                  request.ctpt_rejected === true ||
                  request.ctpt_rejected === "true" ||
                  request.ctpt_rejected === 1 ||
                  request.ctpt_rejected === "1"
                ));
                setProfileRequests(activeRequests);
              } else {
                console.warn("Fallback also returned no records. Response structure:", res.data);
                setProfileRequests([]);
              }
            } catch (fallbackError) {
              console.error("Fallback failed:", fallbackError);
              setProfileRequests([]);
            }
          }
        } catch (parseError) {
          console.error("Error parsing API response:", parseError);
          console.error("Error stack:", parseError.stack);
          console.log("Raw response:", res.data);
          setProfileRequests([]);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log("Error fetching profile requests:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: "Failed to load profile requests",
            code: "",
            display: "show",
            severity: "error",
          },
        });
        setProfileRequests([]);
        setLoading(false);
      });
  }, [dispatch, contactid]);

  useEffect(() => {
    fetchProfileRequests();
  }, [fetchProfileRequests]);

  const getStatusClass = (isApproved) => {
    if (isApproved === true || isApproved === "true" || isApproved === 1) {
      return classes.statusApproved;
    }
    return classes.statusPending;
  };

  const getApprovedText = (isApproved) => {
    if (isApproved === true || isApproved === "true" || isApproved === 1) {
      return "Yes";
    }
    return "No";
  };

  const shouldShowApproveButton = (isApproved) => {
    return !(isApproved === true || isApproved === "true" || isApproved === 1 || isApproved === "1");
  };

  const handleApprove = useCallback((request) => {
    const requestId = request.ctpt_employeeprofilerequestsid || request.id;
    if (!requestId) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Cannot approve: Missing record ID",
          code: "",
          display: "show",
          severity: "error",
        },
      });
      return;
    }

    const api = config.teleportal.submitForm.Update;

    // Payload for Updating the Approval Status to Approved
    const payload = {
      id: requestId,
      recordtype: "ctpt_employeeprofilerequestses",
      data: [
        {
          name: "ctpt_approved",
          value: true,
          type: "boolean",
        },
        {
          name: "ctpt_rejected",
          value: false,
          type: "boolean",
        },
      ],
    };

    console.log("Approving request with payload:", payload);

    post(api, payload)
      .then((res) => {
        console.log("Approve response:", res.data);
        // Check if the update was successful - API might return id or just success
        if (res.data && (res.data.id || res.data === requestId || res.status === 200 || res.status === 204)) {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Profile request approved successfully",
              code: "",
              display: "show",
              severity: "success",
            },
          });
          // Refresh the data
          fetchProfileRequests();
        } else {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to approve profile request",
              code: "",
              display: "show",
              severity: "error",
            },
          });
        }
      })
      .catch((error) => {
        console.error("Error approving profile request:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: error.response?.data?.message || "Error approving profile request. Please try again.",
            code: "",
            display: "show",
            severity: "error",
          },
        });
      });
  }, [dispatch, fetchProfileRequests]);

  const handleReject = useCallback((request) => {
    const requestId = request.ctpt_employeeprofilerequestsid || request.id;
    if (!requestId) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Cannot reject: Missing record ID",
          code: "",
          display: "show",
          severity: "error",
        },
      });
      return;
    }

    const api = config.teleportal.submitForm.Update;
    
    // Payload for Updating the Approval Status to Rejected
    const payload = {
      id: requestId,
      recordtype: "ctpt_employeeprofilerequestses",
      data: [
        {
          name: "ctpt_rejected",
          value: true,
          type: "boolean",
        },
      ],
    };

    console.log("Rejecting request with payload:", payload);

    post(api, payload)
      .then((res) => {
        console.log("Reject response:", res.data);
        // Check if the update was successful - API might return id or just success
        if (res.data && (res.data.id || res.data === requestId || res.status === 200 || res.status === 204)) {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Profile request rejected successfully",
              code: "",
              display: "show",
              severity: "success",
            },
          });
          // Refresh the data
          fetchProfileRequests();
        } else {
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to reject profile request",
              code: "",
              display: "show",
              severity: "error",
            },
          });
        }
      })
      .catch((error) => {
        console.error("Error rejecting profile request:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: error.response?.data?.message || "Error rejecting profile request. Please try again.",
            code: "",
            display: "show",
            severity: "error",
          },
        });
      });
  }, [dispatch, fetchProfileRequests]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toISOString().substring(0, 10);
    } catch (e) {
      return dateString;
    }
  };

  const getEmployerName = (request) => {
    // Handle different formats for lookup fields
    // If ctpt_employer is an object with value property
    if (request.ctpt_employer && typeof request.ctpt_employer === 'object') {
      if (request.ctpt_employer.value !== null && request.ctpt_employer.value !== undefined) {
        return request.ctpt_employer.value;
      }
    }
    // Try formatted value first, then fallback to lookup value
    if (request["_ctpt_employer_value@OData.Community.Display.V1.FormattedValue"]) {
      return request["_ctpt_employer_value@OData.Community.Display.V1.FormattedValue"];
    }
    if (request["_ctpt_employer_value"]) {
      return request["_ctpt_employer_value"];
    }
    if (request["ctpt_employer"] && typeof request["ctpt_employer"] === 'string') {
      return request["ctpt_employer"];
    }
    return "N/A";
  };

  const getEmployeeName = (request) => {
    // Handle different formats for lookup fields
    // If ctpt_employee is an object with value property
    if (request.ctpt_employee && typeof request.ctpt_employee === 'object') {
      if (request.ctpt_employee.value !== null && request.ctpt_employee.value !== undefined) {
        return request.ctpt_employee.value;
      }
    }
    // Try formatted value first, then fallback to lookup value
    if (request["_ctpt_employee_value@OData.Community.Display.V1.FormattedValue"]) {
      return request["_ctpt_employee_value@OData.Community.Display.V1.FormattedValue"];
    }
    if (request["_ctpt_employee_value"]) {
      return request["_ctpt_employee_value"];
    }
    if (request["ctpt_employee"] && typeof request["ctpt_employee"] === 'string') {
      return request["ctpt_employee"];
    }
    return "N/A";
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate pagination
  const paginatedRequests = profileRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleViewRequest = (request) => {
    console.log("Viewing request:", request);
    window.location.href = `/saice-connect/employment/view-profile-requests/${request?.id}`;
  };

  return (
    <ThemeProvider theme={globalTheme}>
      <Heading>
        <h2>Employment - View Profile Requests</h2>
      </Heading>
      <Content>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Typography variant="h5" component="h3" gutterBottom className={classes.title}>
              Profile Requests
            </Typography>
            <Typography variant="body1" gutterBottom style={{ marginBottom: "1rem" }}>
              View and manage profile requests from potential employers and take the next action.
            </Typography>
            {loading ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} aria-label="profile requests table">
                  <TableHead>
                    <TableRow className={classes.tableHeader}>
                      <TableCell>Employer Name</TableCell>
                      <TableCell>Requested By</TableCell>
                      <TableCell>Reason for Contacting</TableCell>
                      <TableCell>Request Date</TableCell>
                      <TableCell>Approved</TableCell>
                      <TableCell align="center" className={classes.actionCell}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {profileRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No profile requests found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRequests.map((request) => (
                        <TableRow key={request.ctpt_employeeprofilerequestsid || request.id} hover>
                          <TableCell>{getEmployerName(request)}</TableCell>
                          <TableCell>{request.ctpt_name || "N/A"}</TableCell>
                          <TableCell>{request.ctpt_reasonforcontacting || "N/A"}</TableCell>
                          <TableCell>{formatDate(request.ctpt_daterequested)}</TableCell>
                          <TableCell>
                            <span className={getStatusClass(request.ctpt_approved)}>
                              {getApprovedText(request.ctpt_approved)}
                            </span>
                          </TableCell>
                          <TableCell align="center" className={classes.actionCell}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              className={classes.actionButton}
                              onClick={() => handleViewRequest(request)}
                            >
                              View
                            </Button>
                            {shouldShowApproveButton(request.ctpt_approved) && (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                className={classes.actionButton}
                                onClick={() => handleApprove(request)}
                              >
                                Approve
                              </Button>
                            )}
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              className={classes.actionButton}
                              onClick={() => handleReject(request)}
                            >
                              Reject
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={profileRequests.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            )}
            <div style={{ marginTop: "1rem" }}>
              <Button
                variant="text"
                onClick={handleGoBack}
                className={classes.goBackButton}
              >
                Go Back
              </Button>
            </div>
          </Grid>
        </Grid>
      </Content>
      {/* <ViewProfileRequestById request={profileRequests[0]} /> */}
    </ThemeProvider>
  );
};

export default ViewProfileRequests;

